import { CctvCameraHandler } from "WebSocket/Handlers/Cctv/CameraHandler";
import { QueueHandler } from "WebSocket/Handlers/Mas/QueueHandler";
import { DmsHandler } from "WebSocket/Handlers/Dms/DmsHandler";
import { ShortEventStatusHandler } from "WebSocket/Handlers/Em/ShortEventStatusHandler";
import { RwisStationHandler } from "WebSocket/Handlers/Rwis/RwisStationHandler";
import { PermissionsHandler } from "WebSocket/Handlers/Saa/PermissionsHandler";
import { DetectorConfigHandler } from "WebSocket/Handlers/Tss/DetectorConfigHandler";
import { DetectorMapHandler } from "WebSocket/Handlers/Tss/DetectorMapHandler";
import { LinkGeometryHandler } from "WebSocket/Handlers/Tss/LinkGeometryHandler";
import { LinkPollDataHandler } from "WebSocket/Handlers/Tss/LinkPollDataHandler";
import { NodesHandler } from "WebSocket/Handlers/Tss/NodesHandler";
import { BaseHandler } from "WebSocket/Handlers/BaseHandler";
import { JwtHandler } from "WebSocket/Handlers/Misc/JwtHandler";
import { SubscriptionTypes } from 'WebSocket/CacheManager/SubscriptionTypes';
import { Message } from 'WebSocket/CacheManager/Message';
import WebSocket from 'WebSocket/BaseWebSocket';
import BroadcastChannel from 'broadcast-channel';
import { LeaderElector, create } from 'broadcast-channel/leader-election';
import { HubConnection } from '@aspnet/signalR';
import { Action, Store } from 'redux';

/**
 * Manages all the caches for objects returned from the WebApi hubs.
 */
export default class CacheManager {
    // The types to subscribe to in the given window.
    subscriptionTypes: SubscriptionTypes[];

    // All of the handlers.
    handlers: BaseHandler<any>[];

    // Callbacks from the handlers based on the subscription types.
    callbacks: { [key: string]: (data: any[]) => void } | undefined;

    /** Callbacks for re-hydration methods. */
    typeCallbacks: {[key: string]: (data: any[]) => any } | undefined;

    // Websocket for connection in the leader.
    webSocket: WebSocket | undefined;

    // The channel to send data across.
    channel: BroadcastChannel<Message>;

    // The one that decides who becomes the leading tab.
    elector: LeaderElector;

    // Store for data.
    static Store: Store<any, Action>;

    store: Store<any, Action>;

    constructor(subscriptionTypes: SubscriptionTypes[], store: Store<any, Action>) {
        // set the subscription types
        this.subscriptionTypes = subscriptionTypes;
        this.subscriptionTypes.push(SubscriptionTypes.Jwt);

        // set parameters and create channel.
        CacheManager.Store = store;
        this.store = store;
        this.handlers = this.createDataHandlerTypes();
        this.channel = new BroadcastChannel('Thin-Client Channel');
        this.registerChannelHandler();

        // set the jwt
        if (!this.store.getState().jwt) {
            this.getJwt();
        }

        // set up the electoral system
        this.elector = create(this.channel);
        this.elector.awaitLeadership().then(() => { this.onLeader(); });
    }

    /*
     * Create all the types for the data handler
     */
    createDataHandlerTypes = () => {
        return [
            new CctvCameraHandler(this.store),
            new DmsHandler(this.store),
            new ShortEventStatusHandler(this.store),
            new JwtHandler(this.store),
            new QueueHandler(this.store),
            new RwisStationHandler(this.store),
            new PermissionsHandler(this.store),
            /*new DetectorConfigHandler(this.store),
            new DetectorMapHandler(this.store),
            new LinkGeometryHandler(this.store),
            new LinkPollDataHandler(this.store),
            new NodesHandler(this.store)*/
        ];
    };

    /*
     * Things to do when the given manager is elected leader.
     */
    onLeader = () => {
        if (this.store.getState().jwt) {
            this.registerWebhookHandlers();
        }
    };

    /*
     * Set up the websocket when the jwt is set if the channel is the leader.
     */
    onJwtSet = () => {
        if (this.elector.isLeader && !this.webSocket) {
            this.registerWebhookHandlers();
        }
        this.channel.postMessage({ messageType: 'modifyJwt', data: [this.store.getState().jwt] });
    }

    clearJwt = () => {
        this.handleMessageWebhook('deleteJwt', '');
    }

    /*
     * Handle when the Webhook receives a message.
     */
    handleMessageWebhook = (messageType: string, data: any) => {
        this.channel.postMessage({ messageType: messageType, data: data });
        if (this.callbacks !== undefined && messageType in this.callbacks) {
            if (messageType.includes('Jwt')) {
                // Gross workaround to make JWT meet base handler signatures
                this.callbacks[messageType].apply(this, [data]);
            } else {
                if (this.typeCallbacks) {
                    this.handleDehydratedData(messageType, data);
                }
            }
        }
    }

    /**
     * Handles when generic object data is de-serialized by the SignalR ClientHub.
     * @param messageType - The message type recieved from a broadcast channel.
     * @param data - The dehydrated Compass/ActiveIts object that is missing prototypes.
     */
    handleDehydratedData = (messageType: string, data: any) => {
        const type = this.parseMessageTypeForSubscription(messageType);
        const rehydratedData: Array<any> = this.typeCallbacks![`rehydrate${type}`].apply(this, data);
        this.callbacks![messageType].apply(this, [rehydratedData]);
    }

    /**
     * Tear down the WebSocket and Channel when the connection is closed.
     */
    stopConnection = () => {
        if (this.webSocket) {
            this.webSocket.StopConnection();
        }
        if (this.channel) {
            this.channel.close();
        }
    }

    /*
     * Get the jwt token through the open thin client channel.
     */
    getJwt = () => {
        this.channel.postMessage({ data: [], messageType: 'getJwtRequest' });
    }

    /*
     * Sets up the Webhook.
     * Register the handlers for what the websocket should do when it receives a specific message.
     */
    registerWebhookHandlers = () => {
        this.webSocket = new WebSocket();

        this.webSocket.InitializeConnection('/ClientHub', this.store.getState().jwt);
        this.webSocket.StartConnection();

        const connection = this.webSocket.connection as HubConnection;
        for (const handler of this.handlers) {
            connection.on(`add${SubscriptionTypes[handler.subscriptionType]}`,
                (data) => this.handleMessageWebhook(`add${SubscriptionTypes[handler.subscriptionType]}`, data));
            connection.on(`modify${SubscriptionTypes[handler.subscriptionType]}`,
                (data) => this.handleMessageWebhook(`modify${SubscriptionTypes[handler.subscriptionType]}`, data));
            connection.on(`delete${SubscriptionTypes[handler.subscriptionType]}`,
                (data) => this.handleMessageWebhook(`delete${SubscriptionTypes[handler.subscriptionType]}`, data));
        }

        // Register response for a getJwtRequest.
        if (this.callbacks) {
            // eslint-disable-next-line dot-notation
            this.callbacks!['getJwtRequest'] = this.sendJwt;
        }
    }

    /*
     * Handler for a getJwtRequest.
     */
    sendJwt = (received: any[]) => {
        if (this.elector.isLeader) {
            // console.log(`Sending JWT - ${this.store.getState().jwt}`);
            this.channel.postMessage({ messageType: 'modifyJwt', data: [this.store.getState().jwt] });
        }
    }

    /**
     * Handle different messages received.
     */
    handleMessage = (received: Message) => {
        if (this.callbacks !== undefined && received.messageType in this.callbacks) {
            if (received.messageType.includes('Jwt')) {
                this.callbacks[received.messageType].apply(this, [received.data]);
            } else {
                if (this.typeCallbacks) {
                    this.handleDehydratedData(received.messageType, received.data);
                }
            }
        }
    }

    /**
     * Gets the subscription type from a given message type string from the client hub connection.
     * @param messageType - The given message type.
     */
    parseMessageTypeForSubscription = (messageType: string) => {
        const regex = /(?:add|modify|delete)/;
        return messageType.replace(regex, '');
    }

    /*
     * Register all of the subscription type handler methods.
     */
    registerChannelHandler = () => {
        this.callbacks = {};
        this.typeCallbacks = {};
        this.handlers.forEach(handler => {
            if (this.subscriptionTypes.indexOf(handler.subscriptionType) > -1) {
                this.typeCallbacks![`rehydrate${SubscriptionTypes[handler.subscriptionType]}`] = handler.rehydratePrototypes;
                this.callbacks![`add${SubscriptionTypes[handler.subscriptionType]}`] = handler.handleAdd;
                this.callbacks![`modify${SubscriptionTypes[handler.subscriptionType]}`] = handler.handleModify;
                this.callbacks![`delete${SubscriptionTypes[handler.subscriptionType]}`] = handler.handleDelete;
            }
        });

        this.channel.addEventListener('message', this.handleMessage);
    };
}